
import React from "react";
import { MapPin, Phone, Users, Calendar, Clock, User, Mail, Globe, Bed } from "lucide-react";
import { useMediaQuery } from "@/hooks/use-media-query";

interface QuickFactsProps {
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  manager?: string;
  personInCharge?: string;
  maxResidents?: number;
  yearEstablished?: number;
  visitingHours?: string;
}

export function QuickFacts({ 
  address, 
  phone, 
  email,
  website,
  manager, 
  personInCharge,
  maxResidents,
  yearEstablished,
  visitingHours = "10:00 AM - 8:00 PM"
}: QuickFactsProps) {
  const isMobile = useMediaQuery("(max-width: 640px)");
  
  return (
    <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2">
      {address && (
        <div className="flex items-start gap-3">
          <div className="mt-1 flex-shrink-0">
            <MapPin className="h-4 w-4 text-grandpal-green" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-medium">Address</span>
            <span className="text-sm text-gray-700">{address}</span>
          </div>
        </div>
      )}
      
      {phone && (
        <div className="flex items-start gap-3">
          <div className="mt-1 flex-shrink-0">
            <Phone className="h-4 w-4 text-grandpal-green" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-medium">Phone</span>
            <a 
              href={`tel:${phone.replace(/\D/g, '')}`} 
              className="text-sm text-grandpal-gray hover:text-grandpal-green py-0.5"
            >
              {phone}
            </a>
          </div>
        </div>
      )}
      
      {email && (
        <div className="flex items-start gap-3">
          <div className="mt-1 flex-shrink-0">
            <Mail className="h-4 w-4 text-grandpal-green" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-medium">Email</span>
            <a 
              href={`mailto:${email}`} 
              className="text-sm text-grandpal-gray hover:text-grandpal-green truncate py-0.5"
            >
              {email}
            </a>
          </div>
        </div>
      )}
      
      {website && (
        <div className="flex items-start gap-3">
          <div className="mt-1 flex-shrink-0">
            <Globe className="h-4 w-4 text-grandpal-green" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-medium">Website</span>
            <a 
              href={website.startsWith('http') ? website : `https://${website}`}
              target="_blank"
              rel="noopener noreferrer" 
              className="text-sm text-grandpal-gray hover:text-grandpal-green truncate py-0.5"
            >
              {website.replace(/^https?:\/\//, '')}
            </a>
          </div>
        </div>
      )}
      
      {personInCharge && (
        <div className="flex items-start gap-3">
          <div className="mt-1 flex-shrink-0">
            <User className="h-4 w-4 text-grandpal-green" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-medium">Person in charge</span>
            <span className="text-sm text-gray-700">{personInCharge}</span>
          </div>
        </div>
      )}
      
      {manager && (
        <div className="flex items-start gap-3">
          <div className="mt-1 flex-shrink-0">
            <User className="h-4 w-4 text-grandpal-green" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-medium">Managed by</span>
            <span className="text-sm text-gray-700">{manager}</span>
          </div>
        </div>
      )}
      
      {maxResidents && (
        <div className="flex items-start gap-3">
          <div className="mt-1 flex-shrink-0">
            <Bed className="h-4 w-4 text-grandpal-green" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-medium">Capacity</span>
            <span className="text-sm text-gray-700">{maxResidents} beds</span>
          </div>
        </div>
      )}
      
      {yearEstablished && (
        <div className="flex items-start gap-3">
          <div className="mt-1 flex-shrink-0">
            <Calendar className="h-4 w-4 text-grandpal-green" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-medium">Established</span>
            <span className="text-sm text-gray-700">{yearEstablished}</span>
          </div>
        </div>
      )}
      
      <div className="flex items-start gap-3">
        <div className="mt-1 flex-shrink-0">
          <Clock className="h-4 w-4 text-grandpal-green" />
        </div>
        <div className="flex flex-col">
          <span className="text-sm font-medium">Visiting hours</span>
          <span className="text-sm text-gray-700">{visitingHours}</span>
        </div>
      </div>
    </div>
  );
}
